import {
  RequestOptions,
  VueHttpClientService,
  VueLoadingService,
  VueLocaleService, VueSecurityQuestionAnswer
} from '@vue/vueframework';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { pluck } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { VueUserNameLookup } from '@vue/vueframework/lib/models/vue-account-username-lookup.interface';
import { AppDefaultsService } from '../../app-defaults.service';
import { PasswordResetDetails } from '../types/password-reset-details';

/**
 * This is primarily for calling the services that allow users
 * to maintain/setup their own account. It is realm agnostic.
 *
 * Many of these endpoints are unauthenticated.
 *
 * Those that are used by your app will need to be white
 * listed in AppDefaultServices.getApplicationsUnAuthenticatedURLList
 * which accepts regex strings.
 *
 * When bringing services over from angular-js-commons
 *   "accountMaintenance" and "accountManagement"
 * are combined here. Methods were renamed where applicable
 * to be more descriptive of their job. However their old name
 * will be found in the description to help with searching.
 */
@Injectable({
  providedIn: 'root'
})
export class AccountManagementService {
  /**
   * Https client, can't use VueHttpClient directly do
   * to circular dependency.
   */
  private httpClient: VueHttpClientService;

  public constructor(
    private defaults: AppDefaultsService,
    private localeService: VueLocaleService,
    vueLoadingService: VueLoadingService,
    http: HttpClient) {
    this.httpClient = new VueHttpClientService(
      http,
      vueLoadingService);
  }

  /**
   * Default options for http calls to apply locale.
   * Not all endpoints requires this, but many
   * do so its put here to simplify method calls below.
   *
   * @param options: additional request options
   * @returns RequestOptions
   */
  public httpOptions(options?: RequestOptions): RequestOptions {
    return {
      headers: new HttpHeaders({
        'Accept-Language': this.localeService.locale
      }),
      ...options
    };
  }

  /**
   * Constructs the base URL for service calls
   * to account management
   * @returns string
   */
  public accountManagementUrl(): string {
    return this.defaults.getPortalRESTRoot() + '/rest/accountManagement/' + this.defaults.getLoginRealm();
  }

  /**
   * This returns security questions based on
   * validating inputs. Used for "retrieve username"
   * @param  username : username
   * @param  scopeID : siteID
   * @returns Observable<VueSecurityQuestionPromptGroup[]>
   */
  public validateUserAndGetSecurityQuestions(
    username: string,
    scopeID: string
  ): Observable<PasswordResetDetails> {
    const url = this.accountManagementUrl() + '/userPasswordResetDetails';
    const options = this.httpOptions({
      params: new HttpParams({
        fromObject: {
          username: username,
          scopeID: scopeID
        }
      })
    });
    return this.httpClient.get(url, options);
  }

  /**
   * API call to get username
   *
   * get user in portal-services for connect
   *
   * @param user Object representing user with name, email, and scopeID
   * @returns Observable<string>
   */
  public userlookup(
    user: VueUserNameLookup): Observable<string> {
    const url = this.accountManagementUrl() + '/userlookup';
    const options = this.httpOptions({
      params: new HttpParams({
        fromObject: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          scopeID: user.siteID
        }
      })
    });
    return this.httpClient.get(url, options).pipe(pluck('username'));
  }
  
  /**
   * Initiates password reset email
   *
   * @param username string
   * @param siteID string
   * @returns Observable<null>
   */
  public initiatePasswordReset(username: string, siteID?: number): Observable<null> {
    const url = this.accountManagementUrl() + '/initiatepasswordreset';

    const payload = {
      locale: this.localeService.locale,
      scopeID: siteID,
      username: username
    };

    return this.httpClient.post(url, payload);
  }
  
  /**
   * Verifies answers to security questions.
   *
   * verifyQuestionAnswers in angular-js-commons
   *
   * @param userId string params from url
   * @param hash string param from the url
   * @param questionAnswers VueSecurityQuestionAnswer
   * @returns Observable<null>
   */
  public verifySecurityQuestionAnswers(
    userId: number,
    hash: string,
    questionAnswers: VueSecurityQuestionAnswer[]): Observable<null> {
    const url = this.accountManagementUrl() + '/usersecurityquestions/validate';
    const options = this.httpOptions({
      params: new HttpParams({
        fromObject: {
          context: 'connectPasswordReset'
        }
      })
    });
    const payload = {
      questionAnswers: questionAnswers,
      user: {
        hash,
        userId
      }
    };
    return this.httpClient.post(url, payload, options);
  }

  /**
     * Update password from the forgot password
     * reset link workflow
     *
     * changePassword - in angular-js-commons
     *
     * @param userId string params from url
     * @param hash string param from the url
     * @param password new password
     * @param questionAnswers VueSecurityQuestionAnswer
     * @returns Observable<null>
     */
  public forgotPasswordUpdatePassword(
    userId: string,
    hash: string,
    password: string,
    questionAnswers: VueSecurityQuestionAnswer[]
  ): Observable<string> {
    const url = this.accountManagementUrl() + '/password';
    const payload = {
      password: password,
      questionAnswers: questionAnswers,
      user: {
        hash,
        userId
      }
    };
    return this.httpClient.post(url, payload).pipe(pluck('scopeID'));
  }
}
